/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import MsDyn365, {
    getCatalogId,
    IComponent,
    IComponentProps,
    IGridSettings,
    IImageSettings,
    msdyn365Commerce,
    TelemetryEvent,
    getUrlSync
} from '@msdyn365-commerce/core';
import { getCartState, ICartActionResult, ICartState } from '@msdyn365-commerce/global-state';
import {
    CartLineValidationResults,
    CartsDataActions,
    format,
    ProductAvailableQuantity,
    ProductDimension,
    ProductPrice,
    ProductsDataActions,
    ProductSearchCriteria,
    ProductSearchResult,
    ProductType,
    SimpleProduct
} from '@msdyn365-commerce/retail-proxy';
import { ErrorNotification, NotificationsManager } from '@msdyn365-commerce-modules/notifications-core';
import { ArrayExtensions, generateProductImageUrl, ObjectExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { getPayloadObject, getTelemetryAttributes, IPopupProps, ITelemetryContent, Popup } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import React, { useState, useMemo } from 'react';
import { IItemsAddedToCartResources, ItemsAddedToCartDialogComponent, MultiItemsSuccessfullyAddedToCartNotification,OrderDetailsProduct } from '@msdyn365-commerce/components';
import { toast, toasts } from '../../../components/toast/toast';
import { getGenericError } from './utilities/error-utilities';
import { IBuyboxCommonResources } from './buyboxInterface';
import { validateAlcoholLicenseAsync } from '../../../actions/validate-alcohol-license.action';
import PQueue from 'p-queue';
import { humanizeUnitOfMeasure } from '../../../components/unit-of-measure';
import PriceComponent from '../../../themes/vinhuset-adventureworks/views/components/price.component';
import { BuyValidationResult } from '../../../themes/vinhuset-adventureworks/views/components/product.component';
import { getDefaultBuyQuantity } from '../../../shared-utils/common-utils';
import { ProductSearchResultExt, SimpleProductExt } from '../../../shared-utils/types';

/**
 * Interface for add to cart resources.
 */
export interface IAddToCartResources {
    goToCartText: string;
    continueShoppingText: string;
    closeNotificationLabel: string;
    headerItemOneText: string;
    headerItemFormatText: string;
    headerLinesFormatText?: string;
    headerMessageText: string;
    freePriceText: string;
    originalPriceText: string;
    currentPriceText: string;
    addedQuantityText: string;
    buyAgainNotificationTitle?: string;
    buyAgainNotificationCloseAriaLabel?: string;
    validationErrorMessage?: string;
    oneErrorText?: string;
    multiErrorsText?: string;
}

export interface IAddToCartToastResources {
    errorHeader?: string;
    productsAddedToCart?: string;
    productsRemovedFromCart?: string;
    productsUpdatedInCart?: string;
    goToCart?: string;
    noAccessToProduct?: string;
}

/**
 * Interface for add to cart components props.
 */
export interface IAddToCartComponentProps extends IComponentProps<{ cart?: ICartState, product: SimpleProduct; price?: ProductPrice; productSearch?: ProductSearchResult | undefined } | undefined> {
    className?: string;
    addToCartText: string;
    updateCartText: string;
    removeFromCartText: string;
    outOfStockText?: string;
    expiredText?: string;
    disabled?: boolean;
    quantity?: number;
    breakoff?: boolean;
    navigationUrl?: string;
    productAvailability?: ProductAvailableQuantity;
    getSelectedProduct?: Promise<SimpleProduct | null>;
    isNavigationToCartPageDisabled?: boolean;
    shouldSkipSiteSettings?: boolean;
    addToCartArialLabel?: string;
    products?: {
        productId: number;
        quantity: number;
        catalogId?: number;
    }[];
    cartQueue?: PQueue;
    orderDetailsProducts?: OrderDetailsProduct[];
    orderDetailsActivePrices?: ProductPrice[];
    hasAvailableProducts?: boolean;
    imageSettings?: IImageSettings;
    gridSettings?: IGridSettings;

    isLoading?: boolean;
    isUpdatingDimension?: boolean;
    isLoadingDeliveryOptions?: boolean;
    isUpdatingDeliveryOptions?: boolean;
    isAddServiceItemToCart?: boolean;
    isAddEmailDeliveryItemToCart?: boolean;
    isPriceKeyedIn?: boolean;
    customPriceAmount?: number;
    isOrderQuantityLimitsFeatureEnabled?: boolean;
    userHasAccess?: boolean;
    buyAlwaysAdd?: boolean;

    dialogStrings?: IAddToCartResources;
    toastStrings?: IAddToCartToastResources;
    buyBoxResources?: IBuyboxCommonResources;

    telemetryContent?: ITelemetryContent;

    catalogId?: number;

    isCustomPriceSelected?: boolean;
    maximumKeyInPrice?: number;
    minimumKeyInPrice?: number;
    defaultMaximumKeyInPrice?: number;
    defaultMinimumKeyInPrice?: number;
    isProductQuantityLoading?: boolean;

    onAdd?(result: ICartActionResult): void;
    onError?(result: IAddToCartFailureResult): void;
    changeUpdatingDimension?(isUpdatingDimension: boolean): void;
    changeUpdatingDeliveryOptions?(isUpdatingDeliveryOptions: boolean): void;
    onUpdateQuantity?(quantity: number): void;
    onBuyValidate?: (product: ProductSearchResultExt, quantity: number) => Promise<BuyValidationResult>;
}

/**
 * Cart action Failure reason type.
 */
export declare type ICartActionFailureReason =
    | 'EMPTYINPUT'
    | 'MISSINGDIMENSION'
    | 'OUTOFSTOCK'
    | 'CARTACTIONFAILED'
    | 'INVALIDCUSTOMAMOUNT';

/**
 * Interface for add to cart failure result.
 */
export interface IAddToCartFailureResult {
    failureReason: ICartActionFailureReason;

    stockLeft?: number;
    cartActionResult?: ICartActionResult;
    missingDimensions?: ProductDimension[];
}

/**
 * This setting defines the experience when a product is added to cart. Corresponds to the configuration in Fabrikam.
 */
export enum AddToCartBehavior {
    /**
     * Navigate to cart page.
     */
    goToCart = 'goToCart',

    /**
     * Show item added to cart popup.
     */
    showModal = 'showModal',

    /**
     * Show mini cart popup.
     */
    showMiniCart = 'showMiniCart',

    /**
     * Show item added to cart notification.
     */
    showNotification = 'showNotification',

    /**
     * Do nothing and stay on the page.
     */
    nothing = 'nothing'
}

const zero = 0;
const defaultQuantity = 1;

/**
 * The propagate result.
 * @param props -- The add to cart component props.
 * @param result -- The cart action result.
 */
const propagateResult = (props: IAddToCartComponentProps, result: ICartActionResult): void => {
    if (props.onAdd) {
        props.onAdd(result);
    }
};

/**
 * The propagate error.
 * @param props -- The add to cart component props.
 * @param result -- The add to cart failure result.
 */
const propagateError = (props: IAddToCartComponentProps, result: IAddToCartFailureResult): void => {
    if (props.onError) {
        props.onError(result);
    }
};

/**
 * Retrieves add to cart input from the component props.
 * @param props - Add to cart component props.
 * @returns Add to cart input.
 */
const getAddToCartInputFromProps = async (props: IAddToCartComponentProps) => {
    const products = props.orderDetailsProducts?.filter(product => !ObjectExtensions.isNullOrUndefined(product.simpleProduct));
    const input = products?.map(product => {
        return {
            product: product.simpleProduct!,
            count: ObjectExtensions.isNullOrUndefined(product.salesLine.Quantity) ? defaultQuantity : product.salesLine.Quantity,
            catalogId: product.salesLine.CatalogId,
            customPrice: props.orderDetailsActivePrices?.find(x => x.ProductId === product.simpleProduct?.RecordId)?.CustomerContextualPrice
        };
    });

    if (ArrayExtensions.hasElements(input)) {
        return input;
    }

    if (ArrayExtensions.hasElements(props.products)) {
        const actionContext = props.context.actionContext;
        const apiSettings = actionContext.requestContext.apiSettings;

        const searchCriteriaInput: ProductSearchCriteria = {
            Context: {
                ChannelId: apiSettings.channelId,
                CatalogId: props.catalogId ?? getCatalogId(props.context.actionContext.requestContext)
            },
            IncludeAttributes: false,
            Ids: props.products.map(product => product.productId)
        };

        const searchResult = await ProductsDataActions.searchByCriteriaAsync({ callerContext: actionContext }, searchCriteriaInput);
        const productSearchResultsWithImages = searchResult.map(productSearchResult => {
            const newProductSearchResult = { ...productSearchResult };
            const newImageUrl = generateProductImageUrl(newProductSearchResult, apiSettings);

            if (newImageUrl) {
                newProductSearchResult.PrimaryImageUrl = newImageUrl;
            }

            return newProductSearchResult;
        });

        return props.products.map(item => {
            const productData = productSearchResultsWithImages.find(product => product.RecordId === item.productId);
            let simpleProduct: SimpleProduct;
            if (productData) {
                simpleProduct = {
                    ...productData,
                    ProductTypeValue: ProductType.Variant,
                    AdjustedPrice: productData.Price,
                    BasePrice: productData.BasePrice ?? productData.Price
                };
            } else {
                // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- Cast to simple product since only record id is available.
                simpleProduct = {
                    RecordId: item.productId
                } as SimpleProduct;
            }

            return {
                product: simpleProduct,
                count: item.quantity,
                catalogId: item.catalogId
            };
        });
    }

    if (props.data?.product) {
        return [
            {
                product: props.data.product,
                count: props.quantity ?? defaultQuantity,
                customPrice: props.data?.productSearch?.Price,
                catalogId: props.catalogId
            }
        ];
    }

    return [];
};

/**
 * Add one item to cart when it is not buy again.
 * @param props -- The props.
 * @param setDisabled -- The set disable call back.
 * @param openModal -- The open modal call back.
 */
const addOneItemToCart = async (
    props: IAddToCartComponentProps,
    cartUrl: string | undefined,
    setDisabled: (disabled: boolean) => void,
    setSuccess: (success: boolean) => void,
    openModal: (opened: boolean) => void,
    cartLineId?: string
): Promise<void> => {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- read config file.
    const addToCartBehavior = props.context.app.config.addToCartBehavior;
    const addToCartInput = await getAddToCartInputFromProps(props);
    const product = addToCartInput[0].product;
    const quantity = addToCartInput[0].count || getDefaultBuyQuantity(product, !!props.breakoff);
    let cartState = await getCartState(props.context.actionContext);

    while (cartState.status === 'UPDATING') {
        console.log('Cart is updating, waiting for it to finish...');
        await new Promise((resolve) => setTimeout(resolve, 1000));
        cartState = await getCartState(props.context.actionContext);
    }

    const addToCartResult = !cartLineId
        ? await cartState.addProductToCart({
            product,
            count: quantity,
            availableQuantity: props.productAvailability?.AvailableQuantity && props.productAvailability.AvailableQuantity>=quantity?props.productAvailability.AvailableQuantity: quantity,
            additionalProperties: { orderQuantityLimitsFeatureIsEnabled: props.isOrderQuantityLimitsFeatureEnabled },
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- read config file.
            enableStockCheck: props.context.app.config.enableStockCheck,
            isPriceKeyedIn: props.isPriceKeyedIn,
            customPrice: props.data?.productSearch?.Price,
            isAddEmailDeliveryItemToCart: props.isAddEmailDeliveryItemToCart,
            shouldSkipSiteSettings: props.shouldSkipSiteSettings,
            catalogId: addToCartInput[0].catalogId
        })
        : await cartState.updateCartLineQuantity({
            cartLineId: cartLineId,
            newQuantity: props.buyAlwaysAdd
                ? (cartState.cart.CartLines?.find(x => x.LineId === cartLineId)?.Quantity || 0) + quantity
                : quantity
        });

    if (addToCartResult.status === 'SUCCESS') {
        if (props.dialogStrings && addToCartBehavior === AddToCartBehavior.showModal) {
            setDisabled(false);
            openModal(true);
        } else if (props.toastStrings && addToCartBehavior === AddToCartBehavior.showNotification) {
            setDisabled(false);
            const toastText = (props.buyAlwaysAdd || !cartLineId)
                ? (props.toastStrings?.productsAddedToCart ?? '{0} {1} added to cart.').replace('{0}', quantity.toString()).replace('{1}', product.Name ?? '')
                : (props.toastStrings?.productsUpdatedInCart ?? 'Updated quantity of {0} to {1} in cart.').replace('{0}', product.Name ?? '').replace('{1}', quantity.toString());

            toast({
                text: toastText,
                type: "success",
                autoClose: 3000,
                position: "bottom-right",
                canClose: true,
                showProgress: true,
                link: cartUrl,
                linkLabel: props.toastStrings?.goToCart
            });
        } else if (
            MsDyn365.isBrowser &&
            props.navigationUrl &&
            !props.isNavigationToCartPageDisabled &&
            (addToCartBehavior === undefined || addToCartBehavior === AddToCartBehavior.goToCart)
        ) {
            window.location.assign(props.navigationUrl);
        } else {
            setDisabled(false);
        }

        if (props.buyAlwaysAdd) {
            props.onUpdateQuantity?.(0);
        } else if (!cartLineId && !props.quantity) {
            // If the quantity is not defined, set it to 1
            props.onUpdateQuantity?.(quantity || 1);
        }

        propagateResult(props, addToCartResult);
        setSuccess(true);
    } else {
        let text = addToCartResult.validationResults && addToCartResult.validationResults.length > 0
            ? addToCartResult.validationResults[0].LocalizedMessage!
            : addToCartResult.errorDetails?.LocalizedMessage;

        if (!text && props.buyBoxResources) {
            text = getGenericError({
                failureReason: 'CARTACTIONFAILED',
                cartActionResult: addToCartResult
            }, cartState, props.buyBoxResources, props.context, product, props.productAvailability, undefined);
        }

        toasts.error(props.toastStrings?.errorHeader, text ?? 'Unknown error adding items to cart');

        propagateError(props, { failureReason: 'CARTACTIONFAILED', cartActionResult: addToCartResult });
        setDisabled(false);
    }
};

/**
 * Callback to handle success of adding to cart.
 * @param props - Add to cart component props.
 * @param setDisabled - Callback to update disabled state of the component.
 * @param setItemsAddedToCartDialogOpen - Callback to update disabled state of the component.
 * @param setErrorMessage - Callback to update error message state of the component.
 * @param cartState - Current cart state.
 * @param addToCartInput - Input used for adding to cart.
 * @param addToCartResult - Result of adding to cart.
 */
const handleAddItemsToCartSuccess = async (
    props: IAddToCartComponentProps,
    setDisabled: (disabled: boolean) => void,
    setItemsAddedToCartDialogOpen: (opened: boolean) => void,
    setErrorMessage: (message: string) => void,
    cartState: ICartState,
    addToCartInput: {
        product: SimpleProduct;
        count: number;
    }[],
    addToCartResult: ICartActionResult
) => {
    // Validate cart for line errors.
    const validationResult: CartLineValidationResults = await CartsDataActions.validateForCheckoutAsync(
        { callerContext: props.context.actionContext, bypassCache: 'none' },
        cartState.cart.Id,
        cartState.cart.Version
    );
    const errorCount = (validationResult.ValidationFailuresByCartLines ?? []).length;
    const singleErrorCount = 1;
    const errorText = errorCount === singleErrorCount ? props.dialogStrings?.oneErrorText : props.dialogStrings?.multiErrorsText;
    const errorMessage =
        errorCount > zero ? format(props.dialogStrings?.validationErrorMessage ?? '', errorCount.toString(), errorText) : '';

    setErrorMessage(errorMessage);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- read config file.
    const addToCartBehavior = props.context.app.config.addToCartBehavior;

    if (props.dialogStrings && addToCartBehavior === AddToCartBehavior.showModal) {
        setDisabled(false);
        setItemsAddedToCartDialogOpen(true);
    } else if (props.dialogStrings && addToCartBehavior === AddToCartBehavior.showNotification) {
        setDisabled(false);
        const itemsAddedResource: IItemsAddedToCartResources = {
            viewCartButtonText: props.dialogStrings.goToCartText,
            closeButtonLabel: props.dialogStrings.closeNotificationLabel,
            itemsAddedToCartHeaderText: props.dialogStrings.headerMessageText,
            itemsAddedToCartFormatText: props.dialogStrings.headerItemFormatText,
            linesAddedToCartFormatText: props.dialogStrings.headerLinesFormatText ?? '{0}',
            itemsAddedValidationErrorMessage: errorMessage
        };

        const notification = new MultiItemsSuccessfullyAddedToCartNotification(
            props.context,
            itemsAddedResource,
            props.imageSettings,
            props.gridSettings,
            addToCartInput,
            props.navigationUrl,
            props.telemetryContent!,
            props.id,
            props.typeName
        );
        NotificationsManager.instance().addNotification(notification);
    } else if (
        MsDyn365.isBrowser &&
        props.navigationUrl &&
        !props.isNavigationToCartPageDisabled &&
        (addToCartBehavior === undefined || addToCartBehavior === AddToCartBehavior.goToCart)
    ) {
        window.location.assign(props.navigationUrl);
    } else {
        setDisabled(false);
    }
    propagateResult(props, addToCartResult);
};

/**
 * Add one or multi item to cart from buy again.
 * @param props -- The props.
 * @param setDisabled -- The set disable call back.
 * @param setItemsAddedToCartDialogOpen -- The open modal call back.
 * @param setErrorMessage -- The set error message call back.
 */
const addItemsToCart = async (
    props: IAddToCartComponentProps,
    setDisabled: (disabled: boolean) => void,
    setSuccess: (success: boolean) => void,
    setItemsAddedToCartDialogOpen: (opened: boolean) => void,
    setErrorMessage: (message: string) => void
): Promise<void> => {
    const addToCartInput = await getAddToCartInputFromProps(props);

    addToCartInput.forEach(item => {
        item.count = item.count || getDefaultBuyQuantity(item.product, !!props.breakoff);
    });

    if (!ArrayExtensions.hasElements(addToCartInput)) {
        return;
    }

    const cartState = await getCartState(props.context.actionContext);
    const addToCartResult = await cartState.addProductsToCart(addToCartInput);

    if (addToCartResult.status === 'SUCCESS') {
        await handleAddItemsToCartSuccess(
            props,
            setDisabled,
            setItemsAddedToCartDialogOpen,
            setErrorMessage,
            cartState,
            addToCartInput,
            addToCartResult
        );
        setSuccess(true);
    } else {
        if (props.dialogStrings?.buyAgainNotificationTitle) {
            // For buy again show simple error notification.
            NotificationsManager.instance().addNotification(
                new ErrorNotification(
                    props.dialogStrings.buyAgainNotificationTitle,
                    props.dialogStrings.buyAgainNotificationCloseAriaLabel ?? ''
                )
            );
        } else {
            NotificationsManager.instance().addNotification(
                new ErrorNotification(
                    addToCartResult.errorDetails?.LocalizedMessage ?? 'Add to cart failed',
                    props.dialogStrings?.closeNotificationLabel ?? ''
                )
            );
        }

        propagateError(props, { failureReason: 'CARTACTIONFAILED', cartActionResult: addToCartResult });
        setDisabled(false);
    }
};

/**
 * Interface for add to cart component.
 */
export interface IAddtoCartComponent extends IComponent<IAddToCartComponentProps> {
    onClick(): (event: React.MouseEvent<HTMLElement>, props: IAddToCartComponentProps) => void;
}

/**
 * On click function.
 * @param _event - The mouse event.
 * @param props - The props.
 * @param setDisabled - Flag to define whether the element is disabled.
 * @param openModal - Flag to specify if it should open in a modal window.
 * @param setItemsAddedToCartDialogOpen - Sets items added to cart while dialog is open.
 * @param setErrorMessage - Error message.
 */
const onClick = async (
    _event: React.MouseEvent<HTMLElement>,
    props: IAddToCartComponentProps,
    cartUrl: string | undefined,
    setWorking: (working: boolean) => void,
    setDisabled: (disabled: boolean) => void,
    setSuccess: (success: boolean) => void,
    openModal: (opened: boolean) => void,
    setItemsAddedToCartDialogOpen: (opened: boolean) => void,
    setErrorMessage: (message: string) => void
): Promise<void> => {
    if (!ArrayExtensions.hasElements(props.products)) {
        const cartError = addToCartError(props);

        if (cartError) {
            propagateError(props, cartError);
            return;
        }

        setDisabled(true);
    }

    const hasOrderDetailsProducts =
        ArrayExtensions.hasElements(props.orderDetailsProducts) && props.orderDetailsProducts.length > defaultQuantity;
    const hasProducts = ArrayExtensions.hasElements(props.products) && props.products.length > defaultQuantity;
    const hasMultipleProducts = hasOrderDetailsProducts || hasProducts;

    let method: 'buy' | 'buy_always' | 'update' | 'remove' = 'buy';
    const cartLine = props.data?.cart?.cart?.CartLines?.find(x => x.ProductId === props.data?.product.RecordId);
    const cartQuantity = cartLine?.Quantity;

    if (props.buyAlwaysAdd) {
        method = 'buy_always';
    } else if (!cartLine || cartQuantity === undefined) {
        method = 'buy';
    } else if (cartQuantity > 0 && props.quantity !== undefined && props.quantity < 1) {
        method = 'remove';
    } else {
        method = 'update';
    }

    setWorking(true);

    const work = async () => {
        if (method === 'buy' || method === 'buy_always' || method === 'update') {
            let userHasAccess: boolean | undefined = props.userHasAccess || true; // TODO: Remove 'true' default here to re-enable alcohol license check
            let validationError: string | undefined;

            if (userHasAccess === undefined) {
                const result = await validateAlcoholLicenseAsync(props.context.actionContext, {
                    CatalogId: props.context.request.apiSettings.catalogId,
                    ProductIds: [props.data!.product.RecordId]
                });
                userHasAccess = result.ValidationResult;
                validationError = props.toastStrings?.noAccessToProduct ?? 'Missing license or access to this product.';
            }

            if (userHasAccess && props.onBuyValidate) {
                const result = await props.onBuyValidate(props.data?.productSearch as ProductSearchResultExt, props.quantity ?? 1);
                userHasAccess = result.isValid;
                validationError = result.message;
            }

            if (userHasAccess !== true) {
                toasts.error(props.toastStrings?.errorHeader, validationError || 'Unknown error');
                setDisabled(false);
                setWorking(false);
                return;
            }

            if (props.shouldSkipSiteSettings && hasMultipleProducts) {
                await addItemsToCart(props, setDisabled, setSuccess, setItemsAddedToCartDialogOpen, setErrorMessage);
            } else {
                await addOneItemToCart(props, cartUrl, setDisabled, setSuccess, openModal, (method === 'update' || method === 'buy_always') ? cartLine?.LineId : undefined);
            }
        } else if (method === 'remove') {
            const result = await props.data?.cart?.removeCartLines({
                cartLineIds: [cartLine?.LineId || 'never']
            });
            if (result?.status === 'SUCCESS') {
                toast({
                    text: (props.toastStrings?.productsRemovedFromCart ?? '{0} removed from cart.').replace('{0}', props.data?.product?.Name ?? ''),
                    type: "success",
                    autoClose: 3000,
                    position: "bottom-right",
                    canClose: true,
                    showProgress: true,
                    link: cartUrl,
                    linkLabel: props.toastStrings?.goToCart
                });

                setSuccess(true);
            } else {
                NotificationsManager.instance().addNotification(
                    new ErrorNotification(
                        result?.errorDetails?.LocalizedMessage ?? 'Add to cart failed',
                        props.dialogStrings?.closeNotificationLabel ?? ''
                    )
                );
                propagateError(props, { failureReason: 'CARTACTIONFAILED', cartActionResult: result });
            }
            setDisabled(false);
        }
    };

    if (props.cartQueue) {
        await props.cartQueue.add(work);
    } else {
        await work();
    }

    setWorking(false);
};

/**
 * Add to cart component action constant.
 */
const AddToCartComponentActions = {
    onClick
};

/**
 * Add to cart component.
 * @param props - The props.
 * @returns - The add to cart component.
 */
export const AddToCartFunctionalComponent: React.FC<IAddToCartComponentProps> = (props: IAddToCartComponentProps) => {
    const [success, setSuccess] = useState<Boolean>(false);
    const [working, setWorking] = useState<Boolean>(false);
    const [disabled, setDisabled] = useState(false);
    const [modalOpen, setModalOpen] = useState(false);
    const [isItemsAddedToCartDialogOpen, setItemsAddedToCartDialogOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [addToCartInput, setAddToCartInput] = useState<{ product: SimpleProduct; count: number; }[]>([]);
    const cartUrl: string | undefined = useMemo(() => getUrlSync('cart', props.context.actionContext), []);

    const onClickHandler = async (event: React.MouseEvent<HTMLElement>) => {
        await AddToCartComponentActions.onClick(event, props, cartUrl, setWorking, setDisabled, setSuccess, setModalOpen, setItemsAddedToCartDialogOpen, setErrorMessage);
    };

    /**
     * Close dialog.
     */
    const closeItemsAddedToCartDialog = React.useCallback(() => {
        setItemsAddedToCartDialogOpen(false);
    }, []);

    const priceComponent = props.data?.price ? (
        <PriceComponent
            data={{ price: props.data.price }}
            context={props.context}
            id={props.id}
            typeName={props.typeName}
            freePriceText={props.dialogStrings?.freePriceText}
            originalPriceText={props.dialogStrings?.originalPriceText}
            currentPriceText={props.dialogStrings?.currentPriceText}
        />
    ) : (
        ''
    );

    const popupProps: IPopupProps = {
        context: props.context,
        className: 'msc-add-to-cart',
        id: props.id,
        typeName: props.typeName,
        data: {
            product: props.data?.product ? {
                ...props.data.product,
                DefaultUnitOfMeasure: humanizeUnitOfMeasure(props.context, props.data.product.DefaultUnitOfMeasure, props.data.product)
            } : undefined,
            price: props.data?.price
        },
        dialogStrings: props.dialogStrings,
        imageSettings: props.imageSettings,
        gridSettings: props.context.request.gridSettings,
        productQuantity: props.quantity !== undefined ? props.quantity : defaultQuantity,
        priceComponent,
        navigationUrl: props.navigationUrl,
        modalOpen,
        setModalOpen,
        telemetryContent: props.telemetryContent
    };

    const itemsAddedResource: IItemsAddedToCartResources = {
        viewCartButtonText: props.dialogStrings?.goToCartText ?? '',
        closeButtonLabel: props.dialogStrings?.closeNotificationLabel ?? 'Close',
        itemsAddedToCartHeaderText: props.dialogStrings?.headerMessageText ?? '',
        itemsAddedToCartFormatText: props.dialogStrings?.headerItemFormatText ?? '',
        linesAddedToCartFormatText: props.dialogStrings?.headerLinesFormatText ?? '{0}',
        itemsAddedValidationErrorMessage: errorMessage
    };

    const renderModalPopup = <Popup {...popupProps} />;
    let label = getLinkText(props);
    const payload = getPayloadObject(TelemetryEvent.AddToCart, props.telemetryContent!, label, '');
    const attributes = getTelemetryAttributes(props.telemetryContent!, payload);
    const buttonClassName = props.shouldSkipSiteSettings ? '' : 'msc-add-to-cart ';
    const cartLine = props.data?.cart?.cart?.CartLines?.find(x => x.ProductId === props.data?.product.RecordId);
    const cartQuantity = cartLine?.Quantity;
    const sameQuantityInCart = !props.buyAlwaysAdd && props.quantity === cartQuantity;

    React.useEffect(() => {
        const retrieveAddToCartInput = async () => {
            const retrievedInput = await getAddToCartInputFromProps(props);
            retrievedInput.forEach(item => {
                item.product = {
                    ...item.product,
                    DefaultUnitOfMeasure: humanizeUnitOfMeasure(props.context, item.product.DefaultUnitOfMeasure, item.product)
                };
            });
            setAddToCartInput(retrievedInput);
        };

        void retrieveAddToCartInput();
    }, [props, setAddToCartInput]);

    React.useEffect(() => {
        if (success) {
            // Wait 2 seconds than set working to undefined
            const timeout = setTimeout(() => {
                setSuccess(false);
            }
            , 2000);
            return () => clearTimeout(timeout);
        } else {
            return undefined;
        }
    }, [success]);

    const product = props.data?.product;
    const soldOutUntilDate = (product as SimpleProductExt | undefined)?.SoldOutUntilDate;
    const soldOutUnknownDate = (product as SimpleProductExt | undefined)?.SoldOutUnknownDate;
    const isExpired =
        soldOutUntilDate && new Date() < new Date(soldOutUntilDate) && (props.productAvailability?.AvailableQuantity || 0) < 1 ||
        (soldOutUnknownDate && (props.productAvailability?.AvailableQuantity || 0) < 1) ||
        product?.Behavior?.IsSaleAtPhysicalStoresAllowed === false ||
        product?.Behavior?.IsSaleAtSelfCheckoutRegistersAllowed === false;
    const disableButton = !product ||
        isExpired ||
        props.disabled ||
        disabled ||
        sameQuantityInCart; // || isIntermediateState(props) <-- Removed cause it broke updating of state

    if (isExpired) {
        label = props.expiredText || 'Utsolgt';
    }

    return (
        <div>
            {renderModalPopup}
            <button
                className={classnames(buttonClassName, props.className, {
                    'loading': working // || isIntermediateState(props)
                    //'success': !working && success
                })}
                aria-label={props.addToCartArialLabel ?? label}
                title={props.addToCartArialLabel ?? label}
                {...attributes}
                onClick={onClickHandler}
                disabled={disableButton}
            >
                {/* {!working && success && <i className="fa-solid fa-check" />} */}
                {label}
            </button>
            <ItemsAddedToCartDialogComponent
                className='msc-lines-added-to-cart-dialog'
                id={props.id}
                typeName={props.typeName}
                context={props.context}
                dialogStrings={itemsAddedResource}
                data={{
                    products: addToCartInput
                }}
                isModalOpen={isItemsAddedToCartDialogOpen}
                onClose={closeItemsAddedToCartDialog}
            />
        </div>
    );
};

// Set default props
AddToCartFunctionalComponent.defaultProps = {
    quantity: defaultQuantity
};

/**
 * Resolves whether product is in stock.
 * @param props - The add to cart component props.
 * @returns Gets the link text.
 */
const getLinkText = (props: IAddToCartComponentProps): string => {
    const cartLine = props.data?.cart?.cart?.CartLines?.find(x => x.ProductId === props.data?.product.RecordId);
    const cartQuantity = cartLine?.Quantity;

    if (props.buyAlwaysAdd) {
        return props.addToCartText;
    } else if (!cartLine || cartQuantity === undefined) {
        return props.addToCartText;
    } else if (cartQuantity > 0 && props.quantity !== undefined && props.quantity < 1) {
        return props.removeFromCartText;
    } else if (cartQuantity !== props.quantity) {
        return props.updateCartText;
    }

    return props.addToCartText;
};

/**
 * Resolves whether product is in stock.
 * @param props - The add to cart component props.
 * @returns The add to cart failure result.
 */
const addToCartError = (props: IAddToCartComponentProps): IAddToCartFailureResult | undefined => {
    const {
        data,
        //productAvailability,
        isCustomPriceSelected,
        customPriceAmount,
        maximumKeyInPrice,
        minimumKeyInPrice,
        defaultMaximumKeyInPrice = 100,
        defaultMinimumKeyInPrice = 10
    } = props;

    if (!data || !data.product.RecordId) {
        // No product exists, won't be able to add to cart
        return { failureReason: 'EMPTYINPUT' };
    }

    if (data.product.Dimensions) {
        const missingDimensions = data.product.Dimensions.filter(
            dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value)
        );

        if (ArrayExtensions.hasElements(missingDimensions)) {
            // At least one dimension with no value exists on the product, won't be able to add to cart
            return { failureReason: 'MISSINGDIMENSION', missingDimensions };
        }
    }

    // if (shouldShowOutOfStock(props, true)) {
    //     const defaultAvailableQuantity = 0; // Showing as out of stock if no available products found.
    //     const availableQuantity = productAvailability?.AvailableQuantity ?? defaultAvailableQuantity;
    //     const stockLeft = Math.max(availableQuantity, defaultAvailableQuantity);
    //
    //     return { failureReason: 'OUTOFSTOCK', stockLeft };
    // }

    // When Custom price is selected, if there is no keyed-in price or keyed-in price is out of limit, should return error.
    if (
        isCustomPriceSelected &&
        (!customPriceAmount ||
            customPriceAmount > (maximumKeyInPrice || defaultMaximumKeyInPrice) ||
            customPriceAmount < (minimumKeyInPrice || defaultMinimumKeyInPrice))
    ) {
        return { failureReason: 'INVALIDCUSTOMAMOUNT' };
    }

    // Only allow adding to cart if not showing out of stock
    return undefined;
};

/**
 * Resolves whether product is in stock.
 * @param props - The add to cart component props.
 * @param includeCurrentQuantity - Flag to specify whether current quantity should be included.
 * @returns The dialog element.
 */
// const shouldShowOutOfStock = (props: IAddToCartComponentProps, includeCurrentQuantity: boolean): boolean => {
//     if (props.context.app.config.enableStockCheck === undefined || props.context.app.config.enableStockCheck === false) {
//         return false;
//     }
//
//     // When skip site settings do not need show out of stock on adding to cart
//     if (props.shouldSkipSiteSettings) {
//         return false;
//     }
//
//     if (
//         props.isLoading ||
//         props.isProductQuantityLoading ||
//         props.isUpdatingDimension ||
//         props.isLoadingDeliveryOptions ||
//         props.isUpdatingDeliveryOptions ||
//         props.isAddServiceItemToCart
//     ) {
//         // Out of stock turn off, don't bother showing out of stock
//         return false;
//     }
//
//     if (!props.data || !props.data.product.RecordId) {
//         // No product exists, don't bother showing out of stock
//         return false;
//     }
//
//     const hasAvailableProducts = props.hasAvailableProducts ?? true;
//     if (!hasAvailableProducts) {
//         return true;
//     }
//
//     if (props.data.product.Dimensions) {
//         if (props.data.product.Dimensions.find(dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value))) {
//             // At least one dimension with no value exists on the product, so also don't show out of stock
//             return false;
//         }
//     }
//
//     const includedQuantityNumber = includeCurrentQuantity && props.quantity ? props.quantity : defaultQuantity;
//
//     return !(
//         props.productAvailability &&
//         props.productAvailability.AvailableQuantity !== undefined &&
//         props.productAvailability.AvailableQuantity >= includedQuantityNumber
//     );
// };

// @ts-expect-error
const isIntermediateState = (props: IAddToCartComponentProps): boolean => {
    if (props.data?.product.Dimensions) {
        if (props.data.product.Dimensions.find(dimension => !(dimension.DimensionValue && dimension.DimensionValue.Value))) {
            // At least one dimension with no value exists on the product, so also not in intermediate state
            return false;
        }
    }

    if (!props.isLoading && !props.isUpdatingDimension && !props.isLoadingDeliveryOptions && !props.isUpdatingDeliveryOptions) {
        return false;
    }

    return true;
};
// @ts-ignore
export const AddToCartComponent: React.FunctionComponent<IAddToCartComponentProps> = msdyn365Commerce.createComponent<IAddtoCartComponent>(
    'AddToCart',
    { component: AddToCartFunctionalComponent, ...AddToCartComponentActions }
);
